'use client';

import React, { useMemo } from 'react';
import type { FC, Key, ReactNode, Ref, RefAttributes } from 'react';
import { createContext } from 'react';
import { ChevronDown } from '@untitledui/icons';
import type { SelectProps as AriaSelectProps } from 'react-aria-components';
import {
    Button as AriaButton,
    ListBox as AriaListBox,
    Select as AriaSelect,
    SelectValue as AriaSelectValue,
} from 'react-aria-components';
import { HintText } from '@milanote/roma/components/base/input/hint-text';
import { Label } from '@milanote/roma/components/base/input/label';
import { TagBase } from '@milanote/roma/components/base/tags/tags';
import { cx } from '@milanote/roma/utils/cx';
import { isReactComponent } from '@milanote/roma/utils/is-react-component';
import { Popover } from './popover';
import { SelectItem, SelectItemBase } from './select-item';
import type { AvatarProps } from '../avatar/avatar';

const SEMANTIC_NAME = 'RomaSelect';

export type SelectItemType = {
    id: string;
    label?: string;
    avatar?: AvatarProps;
    isDisabled?: boolean;
    supportingText?: string;
    icon?: FC | ReactNode;
};

export interface CommonProps {
    hint?: string;
    label?: string;
    tooltip?: string;
    size?: 'sm' | 'md';
    placeholder?: string | FC | ReactNode;
}

export type SelectProps = Omit<AriaSelectProps<SelectItemType>, 'children' | 'items'> &
    RefAttributes<HTMLDivElement> &
    CommonProps & {
        items?: SelectItemType[];
        popoverClassName?: string;
        leadingIcon?: FC | ReactNode;
        /** Alias for `selectedKey` (react-aria). Kept for compatibility with native-like Select APIs. */
        value?: Key | null;
        /** Alias for `onSelectionChange` (react-aria). Kept for compatibility with native-like Select APIs. */
        onChange?: (key: Key | null) => void;
        children: ReactNode | ((item: SelectItemType) => ReactNode);
    };

interface SelectBaseProps {
    isOpen: boolean;
    size: 'sm' | 'md';
    isFocused: boolean;
    isDisabled: boolean;
    isInvalid?: boolean;
    placeholder?: string | FC | ReactNode;
    ref?: Ref<HTMLButtonElement>;
    leadingIcon?: FC | ReactNode;
}

export const sizes = {
    sm: {
        root: 'rounded-xs text-sm',
        select: 'py-sm px-3 *:data-icon:size-4',
        chevron: 'size-4 stroke-[2.5px] -mr-0.5',
    },
    md: {
        root: 'rounded-xs text-md',
        select: 'py-md px-lg *:data-icon:size-5',
        chevron: 'size-5 -mr-1',
    },
};

const SelectBase = (props: SelectBaseProps) => {
    const { isOpen, isFocused, isDisabled, isInvalid, size, placeholder, leadingIcon, ref } = props;

    const placeholderContent = useMemo(() => {
        if (isReactComponent(placeholder)) {
            const Placeholder = placeholder;
            return <Placeholder />;
        }

        return placeholder;
    }, [placeholder]);

    return (
        <AriaButton
            ref={ref}
            className={cx(
                'relative flex w-full cursor-pointer items-center bg-primary ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset',
                (isFocused || isOpen) && 'ring-2 ring-primary_focused',
                isDisabled && 'cursor-not-allowed bg-disabled_subtle text-disabled',
                isInvalid && 'ring-error_subtle',
                sizes[size].root,
            )}
        >
            <AriaSelectValue<SelectItemType>
                className={cx(
                    'flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle',

                    // Icon styles
                    '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled',

                    sizes[size].select,
                )}
            >
                {({ selectedItem }) => {
                    return (
                        <>
                            {selectedItem ? (
                                selectedItem.avatar ? (
                                    <TagBase
                                        size={size}
                                        avatar={selectedItem.avatar}
                                        variant="user"
                                        isDisabled={isDisabled}
                                        className="-my-0.75"
                                    >
                                        {selectedItem.label}
                                    </TagBase>
                                ) : (
                                    <SelectItemBase
                                        label={selectedItem.label}
                                        avatar={selectedItem.avatar}
                                        supportingText={selectedItem.supportingText}
                                        icon={selectedItem.icon || leadingIcon}
                                        size={size}
                                        isDisabled={isDisabled}
                                        className="flex-1 gap-2 p-0"
                                    />
                                )
                            ) : (
                                <SelectItemBase
                                    icon={leadingIcon}
                                    size={size}
                                    isDisabled={isDisabled}
                                    className="flex-1 gap-2 p-0 text-placeholder"
                                >
                                    {placeholderContent}
                                </SelectItemBase>
                            )}

                            <ChevronDown
                                aria-hidden="true"
                                className={cx('ml-auto shrink-0 text-fg-quaternary', sizes[size].chevron)}
                            />
                        </>
                    );
                }}
            </AriaSelectValue>
        </AriaButton>
    );
};

export const SelectContext = createContext<{ size: 'sm' | 'md' }>({ size: 'sm' });

const Select = (props: SelectProps) => {
    const {
        placeholder = 'Select',
        leadingIcon,
        size = 'sm',
        children,
        items,
        label,
        hint,
        tooltip,
        value,
        onChange,
        className,
        selectedKey,
        onSelectionChange,
        ...rest
    } = props;

    const mergedSelectedKey = selectedKey ?? value;
    const mergedOnSelectionChange = React.useCallback(
        (key: Key | null) => {
            onSelectionChange?.(key);
            onChange?.(key);
        },
        [onSelectionChange, onChange],
    );

    return (
        <SelectContext.Provider value={{ size }}>
            <AriaSelect
                {...rest}
                selectedKey={mergedSelectedKey}
                onSelectionChange={mergedOnSelectionChange}
                className={(state) =>
                    cx(
                        SEMANTIC_NAME,
                        'flex flex-col gap-1.5',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            >
                {(state) => (
                    <>
                        {label && (
                            <Label isRequired={state.isRequired} tooltip={tooltip}>
                                {label}
                            </Label>
                        )}

                        <SelectBase {...state} {...{ size, placeholder }} leadingIcon={leadingIcon} />

                        <Popover size={size} className={rest.popoverClassName}>
                            <AriaListBox items={items} className="size-full outline-hidden">
                                {children}
                            </AriaListBox>
                        </Popover>

                        {hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
                    </>
                )}
            </AriaSelect>
        </SelectContext.Provider>
    );
};

Select.displayName = SEMANTIC_NAME;

const _Select = Select as typeof Select & {
    Item: typeof SelectItem;
};
_Select.Item = SelectItem;

export { _Select as Select };
